<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const accountId = computed(() => Number(route.params.accountId));
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const agentAssignments = computed(() => {
  const assignments = [
    {
      key: 'agent_assignment_policy_index',
      title: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.TITLE'),
      description: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.DESCRIPTION'),
      features: [
        {
          icon: 'i-lucide-circle-fading-arrow-up',
          label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.0'),
        },
        {
          icon: 'i-lucide-scale',
          label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.1'),
        },
        {
          icon: 'i-lucide-inbox',
          label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.2'),
        },
      ],
    },
  ];

  // Only show Agent Capacity if BOTH assignment_v2 AND advanced_assignment are enabled
  // advanced_assignment identifies premium users
  const hasAssignmentV2 = isFeatureEnabledonAccount.value(
    accountId.value,
    'assignment_v2'
  );
  const hasAdvancedAssignment = isFeatureEnabledonAccount.value(
    accountId.value,
    'advanced_assignment'
  );

  if (hasAssignmentV2 && hasAdvancedAssignment) {
    assignments.push({
      key: 'agent_capacity_policy_index',
      title: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.TITLE'),
      description: t(
        'ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.DESCRIPTION'
      ),
      features: [
        {
          icon: 'i-lucide-glass-water',
          label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.0'),
        },
        {
          icon: 'i-lucide-circle-minus',
          label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.1'),
        },
        {
          icon: 'i-lucide-users-round',
          label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.2'),
        },
      ],
    });
  }

  return assignments;
});

const handleClick = key => {
  router.push({ name: key });
};
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-10">
    <template #header>
      <BaseSettingsHeader
        :title="$t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')"
        :description="$t('ASSIGNMENT_POLICY.INDEX.HEADER.DESCRIPTION')"
        feature-name="assignment-policy"
      />
    </template>

    <template #body>
      <div class="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <button
          v-for="item in agentAssignments"
          :key="item.key"
          type="button"
          class="flex min-h-64 flex-col rounded-lg border border-slate-200 bg-white p-6 text-left shadow-sm transition hover:border-woot-500 hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
          @click="handleClick(item.key)"
        >
          <div class="flex w-full items-start justify-between gap-4">
            <div>
              <h2
                class="mb-2 text-lg font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ item.title }}
              </h2>
              <p
                class="mb-0 text-sm leading-6 text-slate-600 dark:text-slate-300"
              >
                {{ item.description }}
              </p>
            </div>
            <fluent-icon
              icon="chevron-right"
              size="20"
              class="mt-1 shrink-0 text-woot-500"
            />
          </div>

          <ul class="mt-6 flex flex-col gap-3">
            <li
              v-for="feature in item.features"
              :key="feature.label"
              class="flex items-start gap-3 text-sm leading-5 text-slate-700 dark:text-slate-200"
            >
              <span
                class="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-woot-500"
              />
              <span>{{ feature.label }}</span>
            </li>
          </ul>

          <span
            class="mt-auto pt-6 text-sm font-medium text-woot-500 dark:text-woot-400"
          >
            {{ $t('ASSIGNMENT_POLICY.INDEX.OPEN') }}
          </span>
        </button>
      </div>
    </template>
  </SettingsLayout>
</template>
